import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Modal, Typography, Skeleton, DatePicker, Tooltip } from "antd";
import dayjs from "dayjs";

import testImage from "../../../../assets/images/default-cover-image.jpg";
import DepartmentSelector from "../../../shared/DepartmentSelector";
import PositionSelector from "../../../shared/PositionSelector";
import UserSelector from "../../../shared/UserSelector";
import { ReactComponent as ModuleIcon } from "../../../../assets/images/reusable-items/module.svg";
import { ReactComponent as CourseIcon } from "../../../../assets/images/reusable-items/course.svg";
import { ReactComponent as LessonIcon } from "../../../../assets/images/reusable-items/lesson.svg";
import { ReactComponent as QuizzieIcon } from "../../../../assets/images/reusable-items/quizzie.svg";
import MenuItems from "../../../../assets/images/menu-items";

const { Text } = Typography;

const OnboardingDetailsScreen = (props: any) => {
  const {
    onboarding,
    actions: { getOnboardingById, deleteOnboarding },
  } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    id && getOnboardingById({ id });
  }, []);

  useEffect(() => {
    setSelectedDepartments(onboarding?.departmentIds.map((id: any) => id));
    setSelectedPositions(onboarding?.positionIds.map((id: any) => id));
    setSelectedUsers(onboarding?.userIds.map((id: any) => id));
  }, [onboarding]);

  const onDeleteModule = async () => {
    Modal.confirm({
      title: "Are you sure you want to delete this onboarding?",
      onOk: async () => {
        await deleteOnboarding({ id, navigate });
      },
    });
  };

  const handleDepartmentChange = (departments: string[]) => {
    setSelectedDepartments(departments);
  };

  const handlePositionChange = (positions: string[]) => {
    setSelectedPositions(positions);
  };

  const handleUserChange = (users: string[]) => {
    setSelectedPositions(users);
  };

  if (!onboarding) {
    return <Skeleton active />;
  }

  return (
    <>
      {onboarding && (
        <div className="grid grid-cols-2 w-full gap-8 pb-4">
          <div className="w-full">
            <Text className="text-neutral-600 font-semibold text-lg">
              Onboarding Overview
            </Text>
            <div className="mt-3">
              <div className="bg-white rounded-lg p-6">
                <Text className="text-neutral-600 font-semibold text-sm">
                  Title
                </Text>
                <div className="w-100 mt-2 mb-6 min-h-[50px] bg-neutral-100 p-4 text-neutral-600 text-base font-semibold rounded-lg">
                  <Text>{onboarding.title}</Text>
                </div>

                <Text className="text-neutral-600 font-semibold text-sm">
                  Description
                </Text>
                <div className="w-100 mt-2 mb-6 h-[156px] bg-neutral-100 p-4 text-neutral-600 text-base font-normal rounded-lg overflow-auto">
                  <Text>{onboarding.description}</Text>
                </div>

                <Text className="text-neutral-600 font-semibold text-sm">
                  Cover Image
                </Text>

                <div>
                  <img
                    src={
                      onboarding?.coverImageKey
                        ? `${process.env.REACT_APP_CLOUDFRONT_KEY}/${onboarding?.coverImageKey}`
                        : testImage
                    }
                    alt="cover"
                    className="rounded-lg w-full  mt-2 mb-6"
                  />
                </div>

                <Text className="text-neutral-600 font-semibold text-sm">
                  Deadline
                </Text>

                <DatePicker
                  size="large"
                  className="w-full h-[52px] mt-2 mb-8 px-6"
                  value={
                    onboarding?.deadlineDate && dayjs(onboarding?.deadlineDate)
                  }
                  disabled={true}
                  allowClear={false}
                />

                <div className=" flex flex-col gap-3">
                  <DepartmentSelector
                    departments={onboarding?.assignments?.departments}
                    onSelectionChange={handleDepartmentChange}
                    selectedDepartments={selectedDepartments}
                    setSelectedDepartments={setSelectedDepartments}
                    isDetailsState={true}
                    isAssignmentDetailsPage={false}
                  />
                  <PositionSelector
                    positions={onboarding?.assignments?.positions}
                    onSelectionChange={handlePositionChange}
                    selectedPositions={selectedPositions}
                    setSelectedPositions={setSelectedPositions}
                    isDetailsState={true}
                    isAssignmentDetailsPage={false}
                  />

                  <UserSelector
                    users={onboarding?.assignments?.users}
                    onSelectionChange={handleUserChange}
                    selectedUsers={selectedUsers}
                    setSelectedUsers={setSelectedUsers}
                    isDetailsState={true}
                    isAssignmentDetailsPage={false}
                  />
                </div>
              </div>
              <div className="w-100 flex flex-row justify-end gap-4">
                <Button
                  danger
                  type="primary"
                  className="mt-4 w-2/4 h-[46px] text-base font-semibold"
                  onClick={onDeleteModule}
                >
                  Delete
                </Button>
                <Button
                  type="primary"
                  className="bg-primary-600 mt-4 w-2/4 h-[46px] text-base font-semibold"
                  onClick={() => navigate(`/educator/onboarding/${id}/edit`)}
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
          <div className="w-full">
            {onboarding?.steps?.length > 0 && (
              <Text className="text-neutral-600 font-semibold text-lg">
                Onboarding Steps
              </Text>
            )}
            <div className="h-0 min-h-[calc(100%-40px)] overflow-auto flex flex-col gap-1 mt-3">
              {[...(onboarding?.steps || [])]
                ?.sort((a: any, b: any) => a?.orderIndex - b?.orderIndex)
                ?.map((step: any) => {
                  const item =
                    step.course ||
                    step.module ||
                    step.lesson ||
                    step.quizzie ||
                    step.task;

                  let type = "";
                  let icon = null;
                  if (step.course) {
                    type = "course";
                    icon = <CourseIcon className="size-5 fill-primary-500" />;
                  }
                  if (step.module) {
                    type = "module";
                    icon = <ModuleIcon className="size-5 fill-primary-500" />;
                  }
                  if (step.lesson) {
                    type = "lesson";
                    icon = <LessonIcon className="size-5 fill-primary-500" />;
                  }
                  if (step.quizzie) {
                    type = "quizzie";
                    icon = <QuizzieIcon className="size-5 fill-primary-500" />;
                  }
                  if (step.task) {
                    type = "task";
                    icon = (
                      <MenuItems.Task className="text-xl w-5 text-primary-500 " />
                    );
                  }

                  return item ? (
                    <div
                      onClick={() => navigate(`/educator/${type}/${item.id}`)}
                      key={item.id}
                      className="cursor-pointer"
                    >
                      <div className="w-full h-[50px] bg-white rounded-lg flex items-center pl-4 mb-2 justify-between">
                        <div className="flex items-center gap-4">
                          <div className=" bg-primary-500 text-white size-6 rounded-full flex items-center justify-center">
                            {step.orderIndex}
                          </div>
                          {icon}
                          <Text className="-ml-2">{item.title}</Text>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingDetailsScreen;
